import React, { useState, useEffect } from 'react';
import './MapOverlay.css';
import { useMapStore } from '../store/useMapStore';
import { drivetimeService } from '../../../services/drivetimeService';
import { fetchRecordById } from '../../../services/dataService';
import { getIdField } from '../../../types/metadata';
import { metadataService } from '../../../services/metadataService';
import { DrivetimePolygonPreset, RadiusPreset } from '../../../types/metadata';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface MapOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  overlayType: 'centers' | 'pcp' | 'ml' | null;
  data: any;
}

export const MapOverlay: React.FC<MapOverlayProps> = ({ isOpen, onClose, overlayType, data }) => {
  const [centerTitle, setCenterTitle] = useState<string | null>(null);

  // Reset center title when overlay data changes
  useEffect(() => {
    setCenterTitle(null);
  }, [data]);

  if (!isOpen || !overlayType) return null;

  return (
    <div className="map-overlay">
      <div className="map-overlay-header">
        <h3>{getOverlayTitle(overlayType, { centerTitle })}</h3>
        <button className="map-overlay-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div className="map-overlay-content">
        {overlayType === 'centers' && <CentersOverlay key={`${data?.id}-${data?.coords?.[0]}-${data?.coords?.[1]}`} data={data} onTitleResolved={setCenterTitle} />}
        {overlayType === 'pcp' && <PCPOverlay data={data} />}
        {overlayType === 'ml' && <MLOverlay data={data} />}
      </div>
    </div>
  );
};

const getOverlayTitle = (type: 'centers' | 'pcp' | 'ml', data?: any) => {
  switch (type) {
    case 'centers':
      return data?.centerTitle || 'Center Details';
    case 'pcp':
      return 'Primary Care Provider';
    case 'ml':
      return 'ML Cluster Analysis';
    default:
      return 'Details';
  }
};

const CentersOverlay: React.FC<{ data: any; onTitleResolved?: (title: string | null) => void }> = ({ data, onTitleResolved }) => {
  const layersConfig = useMapStore((state) => state.layersConfig);
  const setCatchmentArea = useMapStore((state) => state.setCatchmentArea);
  const [drivetimeData, setDrivetimeData] = useState<any>(null);
  const [isLoadingDrivetime, setIsLoadingDrivetime] = useState(false);
  const [drivetimeError, setDrivetimeError] = useState<string | null>(null);
  const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>({});
  const [useMultipleYAxes, setUseMultipleYAxes] = useState(true);
  const [recordData, setRecordData] = useState<any>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number>(0);

  // Fetch all layer metadata to resolve layer names for aggregations
  const [allLayerNames, setAllLayerNames] = useState<Record<string, string>>({});
  useEffect(() => {
    metadataService.listMetadata().then((res) => {
      const names: Record<string, string> = {};
      res.files.forEach((f) => { names[f.id] = f.name; });
      setAllLayerNames(names);
    }).catch(() => {});
  }, []);

  // Resolve a layer name: loaded config > metadata API > fallback
  const resolveLayerName = (layerId: string) => {
    const loaded = layersConfig[layerId];
    if (loaded) return loaded.name;
    if (allLayerNames[layerId]) return allLayerNames[layerId];
    return `Layer ${layerId.substring(0, 8)}`;
  };

  // Find the layer config for the clicked marker using the layer ID from the click event
  const centersConfig = React.useMemo(() => {
    // Use the specific layer that was clicked
    const clickedLayerId = data?._layerId;
    if (clickedLayerId && layersConfig[clickedLayerId]) {
      const config = layersConfig[clickedLayerId];
      if (config.viewSettings?.type === 'centers' && config.viewSettings.centersConfig) {
        return { config: config.viewSettings.centersConfig, layerId: clickedLayerId, layerConfig: config };
      }
    }
    
    // Fallback: search for first matching layer (backwards compatibility)
    for (const layerId in layersConfig) {
      const config = layersConfig[layerId];
      if (config.viewSettings?.type === 'centers' && config.viewSettings.centersConfig) {
        return { config: config.viewSettings.centersConfig, layerId, layerConfig: config };
      }
    }
    return null;
  }, [layersConfig, data?._layerId]);

  // Extract coordinates and ID from data
  const coords = data.coords;
  const latitude = coords ? coords[1] : null;
  const longitude = coords ? coords[0] : null;
  const recordId = data.id;

  // Reset state when the selected center changes
  useEffect(() => {
    setRecordData(null);
    setRecordError(null);
    setDrivetimeData(null);
    setDrivetimeError(null);
    setSelectedPresetIndex(0);
  }, [recordId, latitude, longitude]);

  // Load record data when overlay opens
  useEffect(() => {
    if (!centersConfig || !latitude || !longitude || !recordId) {
      if (!recordId) {
        setRecordError('Record ID is missing. All datasets must have an "id" field.');
      }
      return;
    }

    const fetchRecord = async () => {
      setIsLoadingRecord(true);
      setRecordError(null);

      try {
        // Collect all fields we need to fetch
        const fieldsToFetch = new Set<string>();
        
        // Add title and address fields
        if (centersConfig.config.titleField) {
          fieldsToFetch.add(centersConfig.config.titleField);
        }
        if (centersConfig.config.addressField) {
          fieldsToFetch.add(centersConfig.config.addressField);
        }
        
        // Add datapoint fields
        centersConfig.config.datapoints?.forEach((dp: any) => {
          fieldsToFetch.add(dp.field);
        });

        const fields = Array.from(fieldsToFetch);
        
        console.log('[CentersOverlay] Fetching record data', {
          fileId: centersConfig.layerId,
          version: centersConfig.layerConfig.version,
          recordId,
          latitude,
          longitude,
          fields,
        });

        // Get the ID field name from the layer schema
        const idField = centersConfig.layerConfig.schema 
          ? getIdField(centersConfig.layerConfig.schema) 
          : null;

        const record = await fetchRecordById(
          centersConfig.layerId,
          centersConfig.layerConfig.version,
          recordId,
          latitude,
          longitude,
          fields,
          idField || undefined
        );

        console.log('[CentersOverlay] Record data received', record);
        setRecordData(record);
      } catch (error) {
        console.error('[CentersOverlay] Failed to fetch record data', error);
        setRecordError(error instanceof Error ? error.message : 'Failed to load record data');
      } finally {
        setIsLoadingRecord(false);
      }
    };

    fetchRecord();
  }, [centersConfig, latitude, longitude, recordId]);

  // Build combined list of all catchment area presets
  const allPresets = React.useMemo(() => {
    if (!centersConfig) return [];
    const drivetimePresets = centersConfig.config.drivetimePresets || [];
    const radiusPresets = centersConfig.config.radiusPresets || [];
    return [
      ...drivetimePresets.map(p => ({
        type: 'drivetime' as const,
        preset: p,
        name: `${p.stepSize > 0 ? p.start + (p.stepSize * p.steps) : p.start} Min Drivetime`,
      })),
      ...radiusPresets.map(p => ({
        type: 'radius' as const,
        preset: p,
        name: `${p.radius} Mile Radius`,
      })),
    ];
  }, [centersConfig]);

  // Load drivetime/radius data when overlay opens
  useEffect(() => {
    if (!centersConfig || !latitude || !longitude) return;

    const drivetimeAggregations = centersConfig.config.drivetimeAggregations || [];

    // No presets = no aggregation fetching
    if (allPresets.length === 0) return;
    // No aggregations configured = nothing to compute
    if (drivetimeAggregations.length === 0) return;

    const selected = allPresets[selectedPresetIndex];
    if (!selected) return;

    // Build aggregations object (shared for both modes)
    const aggs: Record<string, any> = {};
    drivetimeAggregations.forEach((aggConfig) => {
      const aggName = aggConfig.label || `agg_${drivetimeAggregations.indexOf(aggConfig)}`;
      const aggRequest: any = {
        file: aggConfig.layerId,
        version: aggConfig.version,
        filters: {},
      };
      if (aggConfig.aggregationField) {
        aggRequest.field = aggConfig.aggregationField;
      }
      aggs[aggName] = aggRequest;
    });

    if (selected.type === 'radius') {
      const radiusPreset = selected.preset as RadiusPreset;
      const { radius, stepSize, showEvery } = radiusPreset;
      const steps = stepSize > 0 ? Math.ceil(radius / stepSize) : 0;

      const fetchRadiusData = async () => {
        setIsLoadingDrivetime(true);
        setDrivetimeError(null);

        try {
          // When no steps, still fetch with steps=2 (API minimum) to get aggregation data
          const effectiveSteps = steps < 2 ? 2 : steps;

          console.log('[CentersOverlay] Fetching radius aggregation data', {
            lat: latitude,
            lng: longitude,
            radius,
            steps: effectiveSteps,
            preset: selected.name,
          });

          const response = await drivetimeService.fetchRadius({
            lat: latitude,
            lng: longitude,
            mode: 'radius',
            radius,
            steps: effectiveSteps,
            aggs,
          });

          console.log('[CentersOverlay] Radius aggregation data received', response);
          setDrivetimeData(response);

          // Show catchment on map
          if (latitude && longitude) {
            if (steps > 1) {
              const circlesToShow: { radiusMiles: number; label?: string }[] = [];
              for (let i = 1; i <= steps; i++) {
                if (i % showEvery === 0 || i === steps) {
                  circlesToShow.push({ radiusMiles: stepSize * i, label: `${stepSize * i} mi` });
                }
              }
              setCatchmentArea({ type: 'circles', center: [longitude, latitude], circles: circlesToShow });
            } else {
              setCatchmentArea({ type: 'circle', center: [longitude, latitude], radiusMiles: radius });
            }
          }
        } catch (error) {
          console.error('[CentersOverlay] Failed radius aggregation', error);
          setDrivetimeError(error instanceof Error ? error.message : 'Failed to perform radius aggregation');
        } finally {
          setIsLoadingDrivetime(false);
        }
      };

      fetchRadiusData();
    } else {
      const drivetimePreset = selected.preset as DrivetimePolygonPreset;
      const { start, stepSize, steps, showEvery } = drivetimePreset;

      const fetchDrivetimeData = async () => {
        setIsLoadingDrivetime(true);
        setDrivetimeError(null);

        try {
          // Build times array
          const times: number[] = [];
          if (stepSize === 0 || steps === 0) {
            // No steps — just one time value at start
            times.push(start);
          } else {
            // Only request showEvery intervals (polygons are large)
            for (let i = 0; i <= steps; i++) {
              if (i % showEvery === 0 || i === steps) {
                times.push(start + (i * stepSize));
              }
            }
          }

          console.log('[CentersOverlay] Fetching drivetime data', {
            lat: latitude,
            lng: longitude,
            times,
            preset: selected.name,
          });

          const response = await drivetimeService.fetchDrivetime({
            lat: latitude,
            lng: longitude,
            times,
            aggs,
          });

          console.log('[CentersOverlay] Drivetime data received', response);
          setDrivetimeData(response);

          // Display all returned isochrone polygons on map
          if (response.isochrones) {
            const timeKeys = Object.keys(response.isochrones)
              .map(Number)
              .sort((a, b) => a - b);

            const polygonsToShow: { coordinates: number[][]; label?: string }[] = [];
            for (const t of timeKeys) {
              const iso = response.isochrones[t];
              if (iso && iso.coordinates && iso.coordinates[0]) {
                polygonsToShow.push({ coordinates: iso.coordinates[0], label: `${t} min` });
              }
            }

            if (polygonsToShow.length > 1) {
              setCatchmentArea({ type: 'polygons', polygons: polygonsToShow });
            } else if (polygonsToShow.length === 1) {
              setCatchmentArea({ type: 'polygon', coordinates: polygonsToShow[0].coordinates });
            }
          }
        } catch (error) {
          console.error('[CentersOverlay] Failed to fetch drivetime data', error);
          setDrivetimeError(error instanceof Error ? error.message : 'Failed to load drivetime data');
        } finally {
          setIsLoadingDrivetime(false);
        }
      };

      fetchDrivetimeData();
    }
  }, [centersConfig, latitude, longitude, setCatchmentArea, selectedPresetIndex, allPresets]);

  // Get title and address from fetched record data
  const title = recordData && centersConfig?.config.titleField && recordData[centersConfig.config.titleField]
    ? recordData[centersConfig.config.titleField]
    : isLoadingRecord
    ? 'Loading...'
    : 'Center Details';

  const address = recordData && centersConfig?.config.addressField && recordData[centersConfig.config.addressField]
    ? recordData[centersConfig.config.addressField]
    : null;

  // Report resolved title to parent for overlay header
  useEffect(() => {
    onTitleResolved?.(title !== 'Center Details' && title !== 'Loading...' ? title : null);
  }, [title, onTitleResolved]);

  // Get datapoints from centersConfig
  const datapoints = centersConfig?.config.datapoints || [];

  return (
    <div className="overlay-section">
      {recordError && (
        <div className="overlay-error">Error loading data: {recordError}</div>
      )}
      
      {address && (
        <div className="overlay-field" style={{ justifyContent: 'flex-start' }}>
          <span className="overlay-value" style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
            {address.replace(/,\s*/g, '\n')}
          </span>
        </div>
      )}

      {/* Datapoints Table */}
      {((datapoints.length > 0 && !isLoadingRecord && recordData) || drivetimeData?.aggs) && (
        <div className="overlay-subsection">
          <h5>Data Points</h5>
          <table className="overlay-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {recordData && datapoints.map((datapoint, index) => {
                const value = recordData[datapoint.field];
                const displayValue = value !== undefined && value !== null
                  ? typeof value === 'number'
                    ? value.toLocaleString()
                    : String(value)
                  : 'N/A';

                return (
                  <tr key={index}>
                    <td>{datapoint.label || datapoint.field}</td>
                    <td>{displayValue}</td>
                  </tr>
                );
              })}
              {/* Show aggregation values at the largest polygon/radius */}
              {drivetimeData?.aggs && centersConfig?.config.drivetimeAggregations?.map((aggConfig, index) => {
                const aggName = aggConfig.label || `agg_${index}`;
                const aggResults = drivetimeData.aggs[aggName];
                if (!aggResults) return null;

                const maxTime = Math.max(...Object.keys(aggResults).map(Number));
                const value = aggResults[maxTime];
                if (value === null || value === undefined) return null;

                const layerName = resolveLayerName(aggConfig.layerId);
                const isRadiusMode = allPresets[selectedPresetIndex]?.type === 'radius';
                const label = aggConfig.label
                  ? `${aggConfig.label} (${isRadiusMode ? `${maxTime}mi` : `${maxTime}min`})`
                  : aggConfig.aggregationField
                    ? `${layerName} - ${aggConfig.aggregationField} (${isRadiusMode ? `${maxTime}mi` : `${maxTime}min`})`
                    : `${layerName} - Count (${isRadiusMode ? `${maxTime}mi` : `${maxTime}min`})`;

                const displayValue = typeof value === 'number' ? Math.round(value).toLocaleString() : String(value);

                return (
                  <tr key={`agg-${index}`}>
                    <td>{label}</td>
                    <td>{displayValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Drivetime Aggregations */}
      {allPresets.length > 0 && centersConfig?.config.drivetimeAggregations && centersConfig.config.drivetimeAggregations.length > 0 && (
        <div className="overlay-subsection">
          {/* Catchment Area Preset Dropdown */}
          {allPresets.length > 0 && (
            <div className="config-group" style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Catchment Area</label>
              <select
                value={selectedPresetIndex}
                onChange={(e) => setSelectedPresetIndex(parseInt(e.target.value))}
                className="field-select"
                style={{ width: '100%' }}
              >
                {allPresets.map((preset, idx) => (
                  <option key={idx} value={idx}>
                    {preset.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="subsection-header-with-action">
            <h5>{allPresets[selectedPresetIndex]?.type === 'radius' ? 'Radius Analysis' : 'Drivetime Analysis'}</h5>
            <button 
              className="axis-toggle-button"
              onClick={() => setUseMultipleYAxes(!useMultipleYAxes)}
            >
              {useMultipleYAxes ? 'Multiple Y-Axis' : 'Single Y-Axis'}
            </button>
          </div>
          
          {isLoadingDrivetime && (
            <div className="overlay-loading">
              {allPresets[selectedPresetIndex]?.type === 'radius' 
                ? 'Calculating radius aggregation...' 
                : 'Loading drivetime data...'}
            </div>
          )}

          {drivetimeError && (
            <div className="overlay-error">Error: {drivetimeError}</div>
          )}

          {drivetimeData && drivetimeData.aggs && (
            <div className="drivetime-results">
              {(() => {
                // Transform data for Recharts
                const chartData: any[] = [];
                const aggNames: string[] = [];
                const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#a4de6c'];

                // Format large numbers for Y-axis labels
                const formatYAxis = (value: number) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
                  }
                  if (value >= 1000) {
                    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
                  }
                  return value.toString();
                };

                // Build list of aggregation names and their display labels
                const aggConfigs = centersConfig.config.drivetimeAggregations.map((aggConfig, index) => {
                  const aggName = aggConfig.label || `agg_${index}`;
                  const layerName = resolveLayerName(aggConfig.layerId);
                  const displayLabel = aggConfig.label 
                    ? aggConfig.label
                    : aggConfig.aggregationField 
                      ? `${layerName} - Sum ${aggConfig.aggregationField}` 
                      : `${layerName} - Count`;
                  
                  aggNames.push(aggName);
                  
                  // Initialize visibility state
                  if (visibleLines[aggName] === undefined) {
                    setVisibleLines(prev => ({ ...prev, [aggName]: true }));
                  }
                  
                  return { aggName, displayLabel };
                });

                // Get all time points
                const timePoints = new Set<number>();
                Object.values(drivetimeData.aggs).forEach((aggResults: any) => {
                  Object.keys(aggResults || {}).forEach(time => {
                    timePoints.add(parseInt(time));
                  });
                });

                // Sort time points
                const sortedTimes = Array.from(timePoints).sort((a, b) => a - b);

                // Build chart data
                sortedTimes.forEach(time => {
                  const dataPoint: any = { time };
                  aggConfigs.forEach(({ aggName }) => {
                    const aggResults = drivetimeData.aggs[aggName];
                    const value = aggResults?.[time];
                    dataPoint[aggName] = value !== null && value !== undefined 
                      ? typeof value === 'number' ? Math.round(value) : value
                      : null;
                  });
                  chartData.push(dataPoint);
                });

                const toggleLine = (aggName: string) => {
                  setVisibleLines(prev => ({ ...prev, [aggName]: !prev[aggName] }));
                };

                const isRadiusMode = allPresets[selectedPresetIndex]?.type === 'radius';

                return (
                  <>
                    <ResponsiveContainer width="100%" height={210}>
                      <LineChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="time"
                          ticks={isRadiusMode ? sortedTimes : sortedTimes.filter(t => t % 5 === 0)}
                          tick={{ fontSize: 11 }}
                          tickFormatter={isRadiusMode ? (v: number) => `${v}mi` : undefined}
                        />
                        {useMultipleYAxes ? (
                          // Multiple Y-axes - one for each visible line
                          aggConfigs.map(({ aggName }, index) => {
                            if (visibleLines[aggName] === false) return null;
                            
                            // Only show the first two Y-axes to avoid clutter
                            const visibleIndex = aggConfigs
                              .slice(0, index + 1)
                              .filter(({ aggName: name }) => visibleLines[name] !== false)
                              .length - 1;
                            
                            if (visibleIndex >= 2) return null;
                            
                            return (
                              <YAxis
                                key={aggName}
                                yAxisId={aggName}
                                orientation={visibleIndex === 0 ? 'left' : 'right'}
                                stroke={colors[index % colors.length]}
                                width={35}
                                tickFormatter={formatYAxis}
                                tick={{ fontSize: 11 }}
                              />
                            );
                          })
                        ) : (
                          // Single Y-axis for all lines
                          <YAxis 
                            width={35} 
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 11 }}
                          />
                        )}
                        <Tooltip />
                        {aggConfigs.map(({ aggName, displayLabel }, index) => (
                          visibleLines[aggName] !== false && (
                            <Line
                              key={aggName}
                              type="monotone"
                              dataKey={aggName}
                              name={displayLabel}
                              stroke={colors[index % colors.length]}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 6 }}
                              yAxisId={useMultipleYAxes ? aggName : undefined}
                            />
                          )
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    
                    {/* Toggleable labels */}
                    <div className="chart-legend-controls">
                      {aggConfigs.map(({ aggName, displayLabel }, index) => {
                        const isVisible = visibleLines[aggName] !== false;
                        
                        return (
                          <div 
                            key={aggName} 
                            className={`chart-legend-item ${!isVisible ? 'disabled' : ''}`}
                            onClick={() => toggleLine(aggName)}
                          >
                            <span 
                              className="legend-color-box" 
                              style={{ backgroundColor: colors[index % colors.length] }}
                            />
                            <span className="legend-name">{displayLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Coordinates */}
      {coords && (
        <div className="overlay-field">
          <span className="overlay-label">Coordinates:</span>
          <span className="overlay-value">
            {coords[1].toFixed(6)}, {coords[0].toFixed(6)}
          </span>
        </div>
      )}
    </div>
  );
};

const PCPOverlay: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="overlay-section">
      <h4>Provider Information</h4>
      {data.location_name_from_NPI && (
        <div className="overlay-field">
          <span className="overlay-label">Location:</span>
          <span className="overlay-value">{data.location_name_from_NPI}</span>
        </div>
      )}
      {data.full_address && (
        <div className="overlay-field">
          <span className="overlay-label">Address:</span>
          <span className="overlay-value">{data.full_address}</span>
        </div>
      )}
      {data.provider_count && (
        <div className="overlay-field">
          <span className="overlay-label">Provider Count:</span>
          <span className="overlay-value">{data.provider_count}</span>
        </div>
      )}
      {data.provider_business_city && data.provider_business_state && (
        <div className="overlay-field">
          <span className="overlay-label">City, State:</span>
          <span className="overlay-value">
            {data.provider_business_city}, {data.provider_business_state}
          </span>
        </div>
      )}
      {data.provider_business_postal_code && (
        <div className="overlay-field">
          <span className="overlay-label">Postal Code:</span>
          <span className="overlay-value">{data.provider_business_postal_code}</span>
        </div>
      )}
      {data.coords && (
        <div className="overlay-field">
          <span className="overlay-label">Coordinates:</span>
          <span className="overlay-value">
            {data.coords[1].toFixed(6)}, {data.coords[0].toFixed(6)}
          </span>
        </div>
      )}
    </div>
  );
};

const MLOverlay: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="overlay-section">
      <h4>Cluster Analysis</h4>
      {data.cluster_id !== undefined && (
        <div className="overlay-field">
          <span className="overlay-label">Cluster ID:</span>
          <span className="overlay-value">{data.cluster_id}</span>
        </div>
      )}
      {data.patient_count && (
        <div className="overlay-field">
          <span className="overlay-label">Patient Count:</span>
          <span className="overlay-value">{data.patient_count.toLocaleString()}</span>
        </div>
      )}
      {data.coords && (
        <div className="overlay-field">
          <span className="overlay-label">Centroid:</span>
          <span className="overlay-value">
            {data.coords[1].toFixed(6)}, {data.coords[0].toFixed(6)}
          </span>
        </div>
      )}
      <div className="overlay-info">
        <p>This cluster represents a geographic grouping of patients based on ML analysis.</p>
      </div>
    </div>
  );
};
